import { getDiscussionTopics } from '../../../../Utils/apiService';
import { DiscussionTopic, DiscussionComment } from '../Activities/Forum/ForumDiscussions/types';

// Confirmed real GET DiscussionForum response shape:
// { pending: [...], history: [...], current: [...] } — each topic has
// topicId/name/question/status/postedByName/postedByContactId/createdDate/
// totalComments/comments; each comment has commentId/comment/
// commentedByName/commentedByContactId/createdDate/rating. Not the older
// assumed { topics: [...] } with authorName/createdOn on either.
export interface DiscussionTopicsByStatus {
    pending: DiscussionTopic[];
    history: DiscussionTopic[];
    current: DiscussionTopic[];
}

const mapComment = (raw: any): DiscussionComment => ({
    commentId: raw.commentId || raw.CommentId || '',
    comment: raw.comment || raw.Comment || '',
    commentedByName: raw.commentedByName || raw.CommentedByName || 'Unknown',
    commentedByContactId: raw.commentedByContactId || raw.CommentedByContactId || '',
    createdDate: raw.createdDate || raw.CreatedDate || '',
    rating: raw.rating,
});

const mapTopic = (raw: any): DiscussionTopic => ({
    topicId: String(raw.topicId ?? ''),
    name: raw.name || '',
    question: raw.question || 'Untitled discussion',
    status: raw.status || '',
    postedByName: raw.postedByName || 'Unknown',
    postedByContactId: raw.postedByContactId || '',
    createdDate: raw.createdDate || '',
    totalComments: raw.totalComments ?? (Array.isArray(raw.comments) ? raw.comments.length : 0),
    comments: Array.isArray(raw.comments) ? raw.comments.map(mapComment) : [],
});

const EMPTY_TOPICS: DiscussionTopicsByStatus = { pending: [], history: [], current: [] };

// Keyed by batchId (not contactId) — the standalone faculty ForumDiscussion
// page fans this out once per batch when "All batches" is selected, and the
// embedded per-course CourseDiscussionForum tab fetches the same batchId
// independently. Caching here (same TTL/in-flight/invalidate pattern as
// StudentAssessments.ts's fetchStudentAssessments) means whichever one
// fetches a given batch first warms the cache for the other. Posting a new
// topic or rating a comment must invalidate the affected batch immediately
// — see invalidateDiscussionTopicsCache, called from both write sites.
const CACHE_TTL_MS = 60_000;
const topicsCache = new Map<string, { data: DiscussionTopicsByStatus; fetchedAt: number; inFlight?: Promise<DiscussionTopicsByStatus> }>();

export const invalidateDiscussionTopicsCache = (batchId: string) => {
    topicsCache.delete(batchId);
};

export const fetchDiscussionTopics = async (batchId: string): Promise<DiscussionTopicsByStatus> => {
    if (!batchId) return EMPTY_TOPICS;

    const cached = topicsCache.get(batchId);
    if (cached?.inFlight) return cached.inFlight;
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached.data;

    const request = fetchDiscussionTopicsUncached(batchId)
        .then((data) => {
            topicsCache.set(batchId, { data, fetchedAt: Date.now() });
            return data;
        })
        .catch((error) => {
            topicsCache.delete(batchId);
            throw error;
        });
    topicsCache.set(batchId, { data: cached?.data ?? EMPTY_TOPICS, fetchedAt: cached?.fetchedAt ?? 0, inFlight: request });
    return request;
};

const fetchDiscussionTopicsUncached = async (batchId: string): Promise<DiscussionTopicsByStatus> => {
    const response = await getDiscussionTopics(batchId);
    return {
        pending: Array.isArray(response?.pending) ? response.pending.map(mapTopic) : [],
        history: Array.isArray(response?.history) ? response.history.map(mapTopic) : [],
        current: Array.isArray(response?.current) ? response.current.map(mapTopic) : [],
    };
};
